import { useSessionStorage } from '@cheqroom/hooks';
import { UseMutationResult } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { useDoClearSession } from '../../api/clear-session.api';
import {
	DiscoveryResponse,
	getWorkspaceStrategies,
	resolveDiscoveryWorkspaces,
	useDoDiscovery,
	Workspace,
} from '../../api/discovery.api';
import { Step } from '../../App';
import { generateStrategyUrlForWorkspace } from './utils';

export const useDiscovery = () => {
	const { workspaceId } = useParams<{ workspaceId: string }>();
	const navigateTo = useNavigate();
	const getStrategyLocation = generateStrategyUrlForWorkspace(workspaceId || '');
	const [organisationIdFromSession] = useSessionStorage<string | undefined>('organisation');

	const queryData = useDoDiscovery<DiscoveryResponse>(
		{ organisationId: organisationIdFromSession },
		{
			cacheTime: 0,
			onSuccess: (response) => {
				const workspaces = getWorkspaceStrategies(response, workspaceId);

				if (workspaces.length === 0) {
					return navigateTo(Step.SELECT_WORKSPACE, { replace: true });
				}

				if (workspaces.length === 1) {
					return navigateTo(getStrategyLocation(workspaces[0].strategy));
				}
			},
		}
	);

	const resolvedWorkspaces = resolveDiscoveryWorkspaces(queryData.data);
	const strategies = getWorkspaceStrategies(queryData.data, workspaceId);

	const data = {
		email: queryData.data?.email,
		workspace: {
			...strategies?.[0],
		} as Workspace,
		workspaces: resolvedWorkspaces,
	};

	return { ...queryData, data, discovery: queryData.data, getStrategyLocation };
};

export const useClearSession = (): UseMutationResult<void, unknown, void, unknown> => {
	const navigateTo = useNavigate();

	return useDoClearSession({
		onSettled: () => {
			navigateTo(Step.ENTER_EMAIL, { replace: true });
		},
	});
};
